import { useRef, useMemo } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

// We need a deep import for the context hook to avoid a circular dependency,
// where we'd import useInvitePeopleContext from InvitePeopleContext.tsx (or InvitePeople/index.ts),
// and they'd import useAnalytics from us.
// Webpack would give us a cryptic runtime TypeError about bad "super expression".
import { useInvitePeopleContext } from '../components/InvitePeople/InvitePeopleContextInternal'; // Need deep import!
import type { AnalyticsPayload } from '../types';

import {
	type AnalyticsClassConstructor,
	type BaseInvitePeopleAnalytics,
} from './BaseInvitePeopleAnalytics';
import { type CommonAttributes, useCommonAttributesFrom } from './commonAttributes';
import { fireAnalyticsEvent } from './fireAnalyticsEvent';

export const useAnalytics = <Instance extends BaseInvitePeopleAnalytics>(
	AnalyticsClass: AnalyticsClassConstructor<Instance>,
): Instance =>
	useAnalyticsWithAttributes(AnalyticsClass, useCommonAttributesFrom(useInvitePeopleContext()));

export const useAnalyticsWithAttributes = <Instance extends BaseInvitePeopleAnalytics>(
	AnalyticsClass: AnalyticsClassConstructor<Instance>,
	commonAttributes: CommonAttributes,
): Instance => {
	const { fireAnalytics } = useFireInvitePeopleAnalytics();

	// Why ref and .setCommonAttributes?
	// We want to keep returning the same instance even if attributes change,
	// as many data fetching effects depend on analytics.
	// Attributes can still change, e.g. if they are loaded asynchronously.
	const attributesRef = useRef<typeof commonAttributes>();
	attributesRef.current = commonAttributes;

	const memoizedInstance = useMemo(
		() =>
			new AnalyticsClass(
				fireAnalytics,
				attributesRef.current!, // refs don't constitute dependencies
			),
		[AnalyticsClass, fireAnalytics],
	);

	// Ensures events get the newest attributes when fired,
	// even if attributes changed after the instance was created.
	memoizedInstance.setCommonAttributes(attributesRef.current);

	return memoizedInstance;
};

export type FireAnalyticsPayload = (payload: AnalyticsPayload) => void;

const useFireInvitePeopleAnalytics = (): {
	fireAnalytics: FireAnalyticsPayload;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useMemo(
		() => ({
			fireAnalytics: (eventPayload: AnalyticsPayload) =>
				fireAnalyticsEvent(eventPayload, createAnalyticsEvent),
		}),
		[createAnalyticsEvent],
	);
};
